import React, { Fragment, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import moment from 'moment';
import { Box, CssBaseline, Drawer } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl } from 'react-intl';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';

import useGlobalContext from '../../common/hooks/useGlobalContext';
import styles from './styles';
import NewRidePanel from './NewRidePanel/NewRidePanel';
import PopupModal from '../../common/elements/PopupModal';
import LeftPanel from './LeftPanel';
import LeftMenu from './LeftMenu/LeftMenu';
import ProfileFormPanel from './ProfileFormPanel/ProfileFormPanel';
import RideDetails from './LeftPanelTabs/RideDetails';
import RightDetailsPanel from '../../common/elements/RightDetailsPanel';
import { history } from '../../history';
import RideInfoTab from './LeftPanelTabs/RideInfoTab';
import UserInfoTab from './LeftPanelTabs/UserInfoTab';
import CompanyInfoTab from './LeftPanelTabs/CompanyInfoTab';
import messages from './messages';

const ADMIN = 'ADMIN';
const RIDER = 'RIDER';
const USER = 'USER';
const LIVE_TRIPS = 'liveTrips';
const SCHEDULED_TRIPS = 'scheduledTrips';
const PAST_TRIPS = 'pastTrips';

export const HomePage = (props) => {
  const {
    classes,
    intl: { formatMessage },
  } = props;
  const [isCarsharing, setIsCarsharing] = useState(false);
  const [riders, setRiders] = useState([]);
  const [users, setUsers] = useState([]);
  const [userPage, setUserPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalInvites, setTotalInvites] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [rides, setRides] = useState([]);
  const [scheduledRides, setScheduledRides] = useState([]);
  const [pastRides, setPastRides] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rightSidePanelOpen, setRightSidePanelOpen] = useState(false);
  const [loader, setLoader] = useState(false)
  const {
    state: {
      activeBusinessId,
      activeService,
      activeServiceId,
      associatedBusinesses,
      error,
      tabState,
      tenantSettings,
    },
    setActiveBusinessId,
    setAssociatedBusinesses,
    setActiveService,
    setActiveServiceId,
    setError,
    setServices,
    setTabState,
    setTenantSettings,
  } = useGlobalContext();

  useEffect(() => {
    const fetchIdentity = () => {
      return axios
        .get('/api/v3/identity')
        .then(
          ({
            data: {
              is_carsharing_business_admin,
              roles: {
                business_admin: { business_entities, services },
              },
            },
          }) => {
            setIsCarsharing(is_carsharing_business_admin);
            setAssociatedBusinesses(business_entities);
            setActiveBusinessId(business_entities[0].id);
            if (is_carsharing_business_admin) {
              setTabState('profile');
            }
            setServices(services);
            setActiveServiceId(services[0].id);
          }
        )
        .catch(() => history.push('/login'));
    };
    const fetchSettings = () => {
      return axios.get('/api/v2/settings').then(({ data }) => setTenantSettings(data));
    };

    const initialFetch = async () => {
      await Promise.all([fetchSettings(), fetchIdentity()]).catch((e) => setError(e));
    };

    initialFetch();
    return function cleanup() {
      setIsCarsharing(false);
      setRiders([]);
      setUsers([]);
      setUserPage(1);
      setAdmins([]);
      setRides([]);
      setScheduledRides([]);
      setPastRides([]);
      setDrawerOpen(false);
      setRightSidePanelOpen(false);
      setAssociatedBusinesses([]);
      setActiveBusinessId(null);
      setServices([]);
      setActiveServiceId(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUserPage(1);
  }, [activeBusinessId]);

  useEffect(() => {
    const fetchActiveService = (id) => {
      isCarsharing
        ? axios.get(`/api/v3/carsharing_services/${id}`).then(({ data }) => setActiveService(data))
        : axios.get(`/api/v2/ridesharingservices/${id}`).then(({ data }) => setActiveService(data));
    };
    if (activeServiceId) {
      fetchActiveService(activeServiceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServiceId, isCarsharing]);

  const fetchRides = useCallback(
    (search) => {
      const params = {
        business_entity_ids: activeBusinessId,
        expand: 'driver.vehicle',
        ordering: '-created_at',
        page: 1,
        page_size: 500,
        service_id: activeServiceId,
        search,
        timestamp: moment().unix(),
      };
      return axios.get('/api/v2/livetrips', { params }).then(({ data }) => setRides(data.results));
    },
    [activeBusinessId, activeServiceId, setRides]
  );
  const fetchScheduledTrips = useCallback(
    (search) => {
      const params = {
        business_entity_ids: activeBusinessId,
        ordering: 'scheduled_for_pickup_at',
        page: 1,
        page_size: 500,
        search,
        service_id: activeServiceId,
        timestamp: moment().unix(),
      };
      return axios
        .get('/api/v2/scheduledtriprequests', { params })
        .then(({ data }) => setScheduledRides(data.results));
    },
    [activeBusinessId, activeServiceId, setScheduledRides]
  );

  const fetchPastTrips = useCallback(
    (search) => {
      const params = {
        all_trips: true,
        business_entity_ids: activeBusinessId,
        expand: 'driver.vehicle',
        is_completed: true,
        ordering: '-created_at',
        page: 1,
        page_size: 500,
        search,
        service: activeServiceId,
        timestamp: moment().unix(),
      };
      return axios.get('/api/v2/trips', { params }).then(({ data }) => setPastRides(data.results));
    },
    [activeBusinessId, activeServiceId, setPastRides]
  );

  const fetchAdmins = useCallback(
    (search) => {
      const params = {
        business_entity_ids: activeBusinessId,
        ordering: '-id',
        search,
      };
      return axios.get('/api/v2/staff', { params }).then(({ data }) => setAdmins(data));
    },
    [activeBusinessId, setAdmins]
  );

  const fetchRiders = useCallback(
    (search) => {
      const params = {
        business_entity_ids: activeBusinessId,
        timestamp: moment().unix(),
        all_customers: true,
        ordering: '-id',
        search,
      };
      return axios.get('/api/v2/customers', { params }).then(({ data }) => setRiders(data));
    },
    [activeBusinessId, setRiders]
  );

  const fetchUsers = useCallback(() => {
    const params = {
      business_entity_id: activeBusinessId,
      ordering: '-id',
      page: userPage,
    };
    return axios
      .get('/api/v3/user_invites', { params })
      .then(({ data: { object_count, page_size, results } }) => {
        setUsers(results);
        setPageSize(page_size);
        setTotalInvites(object_count);
      });
  }, [activeBusinessId, setUsers, userPage]);

  const handleUserPageChange = (e, value) => {
    setUserPage(value);
  };

  useEffect(() => {
    if (activeBusinessId) {
      fetchAdmins();
      if (isCarsharing) {
        const params = {
          business_entity_id: activeBusinessId,
          ordering: '-id',
          page: userPage,
        };
        axios
          .get('/api/v3/user_invites', { params })
          .then(({ data: { object_count, page_size, results } }) => {
            setUsers(results);
            setPageSize(page_size);
            setTotalInvites(object_count);
          });
      } else {
        fetchRiders();
      }
    }
  }, [activeBusinessId, fetchAdmins, fetchRiders, isCarsharing, userPage]);

  const toggleDrawer = () => setDrawerOpen((prevOpen) => !prevOpen);
  const renderLeftPanel = () => {
    const profileTabs = [
      {
        component: <CompanyInfoTab isCarsharing={isCarsharing} key="companyInfo" />,
        label: formatMessage(messages.companyInfo),
      },
      ...[
        !isCarsharing && {
          component: (
            <UserInfoTab
              key="userInfoAdmin"
              updateInfo={fetchAdmins}
              tabType={ADMIN}
              info={admins}
            />
          ),
          label: formatMessage(messages.admins),
        },
      ],
      {
        component: (
          <UserInfoTab
            key={isCarsharing ? 'userInfo' : 'userInfoRiders'}
            info={isCarsharing ? users : riders}
            isCarsharing={isCarsharing}
            handleChange={handleUserPageChange}
            pageSize={pageSize}
            setLoader={(status) => setLoader(status)}
            setRightSidePanelOpen={setRightSidePanelOpen}
            tabType={isCarsharing ? USER : RIDER}
            totalInvites={totalInvites}
            userPage={userPage}
          />
        ),
        label: isCarsharing
          ? formatMessage(messages.userTotal, { numUsers: totalInvites })
          : formatMessage(messages.riders),
      },
    ];
    const createTabs = [
      {
        component: (
          <RideInfoTab key="liveTrips" fetchTrips={fetchRides} tabType={LIVE_TRIPS} info={rides} />
        ),
        label: formatMessage(messages.current),
      },
      {
        component: (
          <RideInfoTab
            key="scheduleTrips"
            fetchTrips={fetchScheduledTrips}
            tabType={SCHEDULED_TRIPS}
            info={scheduledRides}
          />
        ),
        label: formatMessage(messages.upcoming),
      },
      {
        component: (
          <RideInfoTab
            key="pastTrips"
            fetchTrips={fetchPastTrips}
            tabType={PAST_TRIPS}
            info={pastRides}
          />
        ),
        label: formatMessage(messages.past),
      },
    ];

    switch (tabState) {
      case 'profile':
        return (
          <LeftPanel
            initialTab={'companyInfo'}
            isCarsharing={isCarsharing}
            setRightSidePanelOpen={setRightSidePanelOpen}
            tabs={profileTabs.filter(Boolean)}
            title={isCarsharing ? formatMessage(messages.users) : formatMessage(messages.info)}
          />
        );
      case 'create':
        return (
          <LeftPanel
            initialTab={'current'}
            tabs={createTabs}
            title={formatMessage(messages.allRides)}
          />
        );
      default:
        return null;
    }
  };

  const isActiveBusiness =
    associatedBusinesses && activeBusinessId
      ? associatedBusinesses.find((business) => business.id === activeBusinessId).is_active
      : false;

  return (
    <Box className={classes.root}>
      {associatedBusinesses && tenantSettings && activeService && (
        <Fragment>
          <CssBaseline />
          <Drawer
            variant="permanent"
            className={clsx(classes.drawer, {
              [classes.drawerOpen]: drawerOpen,
              [classes.drawerClose]: !drawerOpen,
            })}
            classes={{
              paper: clsx({
                [classes.drawerOpen]: drawerOpen,
                [classes.drawerClose]: !drawerOpen,
              }),
            }}
          >
            <LeftMenu isCarsharing={isCarsharing} open={drawerOpen} toggleDrawer={toggleDrawer} />
          </Drawer>
          <Box className={clsx(classes.flex, classes.panelContainer)}>
            <Box ml={5}>{renderLeftPanel()}</Box>
            <RightDetailsPanel>
              <RideDetails fetchRides={fetchRides} fetchScheduledTrips={fetchScheduledTrips} />
            </RightDetailsPanel>
            {isActiveBusiness && !isCarsharing && (
              <Box mr={5}>
                {tabState === 'create' ? (
                  <NewRidePanel
                    fetchRides={fetchRides}
                    fetchScheduledTrips={fetchScheduledTrips}
                    passengerTypes={tenantSettings && tenantSettings.passenger_types}
                  />
                ) : (
                  <ProfileFormPanel fetchAdmins={fetchAdmins} fetchRiders={fetchRiders} />
                )}
              </Box>
            )}
            {isActiveBusiness && rightSidePanelOpen && (
              <Box mr={5}>
                <ProfileFormPanel
                  fetchUsers={fetchUsers}
                  isCarsharing={isCarsharing}
                  setLoader={(status) => setLoader(status)}
                  setRightSidePanelOpen={setRightSidePanelOpen}
                />
              </Box>
            )}
          </Box>
          <PopupModal
            open={error.hasError}
            onClose={() => setError()}
            message={error.errorMessage}
          />
        </Fragment>
      )}
      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};
export default withStyles(styles)(injectIntl(HomePage));
